/**
 * AI 文书订单 / 付费 + AI 会话 REST API hooks
 *
 * 文书订单 (B-3A):
 * - GET  /client/legal/ai/document/quote/{documentId}      报价 (是否需付费 / 是否可下载)
 * - GET  /client/legal/ai/document/preview/{documentId}    服务端图片/PDF 预览元信息
 * - POST /client/legal/ai/document/order/prepare           准备订单
 * - POST /client/legal/ai/document/order/unlock            会员免费解锁
 * - POST /client/legal/ai/document/order/pay               创建/恢复商业购买单
 * - GET  /client/legal/ai/document/order/{payOrderNo}      查询订单详情
 *
 * AI Conversation (B-3B):
 * - GET    /app/legal/ai/conversations                         当前主体会话列表
 * - GET    /app/legal/ai/conversations/{conversationId}        会话详情
 * - GET    /app/legal/ai/conversations/{conversationId}/messages 消息历史
 * - PUT    /app/legal/ai/conversations/{conversationId}/title  修改标题
 * - PUT    /app/legal/ai/conversations/{conversationId}/pin    置顶/取消置顶
 * - DELETE /app/legal/ai/conversations/{conversationId}        删除会话
 *
 * Web 支付:
 * - POST /app/pay/wechat/prepay                            多场景微信预支付 (H5/NATIVE/JSAPI/APP)
 *
 * 展示配置:
 * - GET  /client/legal/ai/display/current                  当前启用的 AI 标题和描述
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiBaseURL, http } from '@/lib/http'
import { BizError } from '@/types/api'
import { APP_TITLE } from '@/config/app'
import { useAuthStore } from '@/stores/auth'
import type {
  LegalAiDisplayConfigVo,
  LegalAiDocumentCheckoutBo,
  LegalAiDocumentCheckoutVo,
  LegalAiDocumentOrderPayBo,
  LegalAiDocumentOrderVo,
  LegalAiDocumentPreviewVo,
  LegalAiDocumentQuoteVo,
  LegalAiConversationMetaVo,
  LegalAiConversationPinRequest,
  LegalAiConversationTitleRequest,
  LegalAiGuestAccessRequest,
  LegalAiGuestAccessVo,
  LegalAiGuestAssetPolicyVo,
  LegalAiGuestAssetVo,
  LegalAiVoiceTranscriptionVo,
  LegalAiMessageVo,
  PageQuery,
  PayWechatPrepayRequestBo,
  PayWechatPrepayVo,
  TableDataInfo,
} from '@/types/api'

/** AI 文书订单状态 (后端 0/10=待支付 20=已支付 90=已关闭) */
export const PAY_ORDER_STATUS = {
  PENDING: 10,
  PAID: 20,
  CLOSED: 90,
} as const

export const DEFAULT_AI_DISPLAY_CONFIG: Required<LegalAiDisplayConfigVo> = {
  title: APP_TITLE,
  description:
    '请简要描述您遇到的法律问题或需要生成的文书场景，我将先进行案由分析并引导您完成问卷。',
}

const AI_CLIENT_BASE = '/client/legal/ai'
const AI_CONVERSATION_BASE = '/app/legal/ai/conversations'
const AI_DISPLAY_TEMPLATE_CODE = 'legal_web'

export const AI_SESSION_HISTORY_PAGE = { pageNum: 1, pageSize: 80 } as const satisfies PageQuery

function useAiConversationSubjectKey(): 'user' | 'guest' {
  return useAuthStore((state) => (state.accessToken ? 'user' : 'guest'))
}

function toAiSessionUpdateTime(session: LegalAiConversationMetaVo): number {
  return (
    new Date((session.lastMessageTime ?? session.startTime ?? '').replace(/-/g, '/')).getTime() || 0
  )
}

export function compareAiSessionsByUpdateTimeDesc(
  a: LegalAiConversationMetaVo,
  b: LegalAiConversationMetaVo,
): number {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
  const aPinnedTime = new Date((a.pinnedTime ?? '').replace(/-/g, '/')).getTime() || 0
  const bPinnedTime = new Date((b.pinnedTime ?? '').replace(/-/g, '/')).getTime() || 0
  if (aPinnedTime !== bPinnedTime) return bPinnedTime - aPinnedTime
  const aTime = toAiSessionUpdateTime(a)
  const bTime = toAiSessionUpdateTime(b)
  if (aTime !== bTime) return bTime - aTime
  return (b.conversationId ?? '').localeCompare(a.conversationId ?? '')
}

/* ---------- 展示配置 ---------- */

/** 查询当前启用的 AI 标题和描述 (公开接口, 未登录可访问) */
export function useAiDisplayConfig() {
  return useQuery({
    queryKey: ['ai-doc', 'display', 'current', AI_DISPLAY_TEMPLATE_CODE],
    queryFn: () =>
      http.get<LegalAiDisplayConfigVo>(`${AI_CLIENT_BASE}/display/current`, {
        params: { templateCode: AI_DISPLAY_TEMPLATE_CODE },
      }),
    staleTime: 5 * 60_000,
  })
}

/* ---------- 报价 ---------- */

/** 查询 AI 文书下载报价 (是否需付费 / 是否可下载) */
export function useDocumentQuote(documentId: string | undefined) {
  return useQuery({
    queryKey: ['ai-doc', 'quote', documentId],
    queryFn: () =>
      http.get<LegalAiDocumentQuoteVo>(`${AI_CLIENT_BASE}/document/quote/${documentId}`),
    enabled: Boolean(documentId),
    staleTime: 30_000,
  })
}

/** 查询 AI 文书服务端预览元信息 */
export function useAiDocumentPreview(documentId: string | undefined) {
  return useQuery({
    queryKey: ['ai-doc', 'preview', documentId],
    queryFn: () =>
      http.get<LegalAiDocumentPreviewVo>(
        `${AI_CLIENT_BASE}/document/preview/${encodeURIComponent(documentId ?? '')}`,
      ),
    enabled: Boolean(documentId),
    staleTime: 30_000,
  })
}

/* ---------- 订单 ---------- */

/** 查询订单详情 (按 payOrderNo); 传 refetchInterval 可启用轮询 */
export function useDocumentOrder(
  payOrderNo: string | undefined,
  opts: { refetchInterval?: number | false } = {},
) {
  return useQuery({
    queryKey: ['ai-doc', 'order', payOrderNo],
    queryFn: () =>
      http.get<LegalAiDocumentOrderVo>(`${AI_CLIENT_BASE}/document/order/${payOrderNo}`),
    enabled: Boolean(payOrderNo),
    refetchInterval: opts.refetchInterval ?? false,
    staleTime: 0,
  })
}

/** 准备订单状态 (不创建支付单) */
export function usePrepareDocumentOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['ai-doc', 'prepare'],
    mutationFn: async (body: LegalAiDocumentCheckoutBo) =>
      http.post<LegalAiDocumentOrderVo>(`${AI_CLIENT_BASE}/document/order/prepare`, body),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['ai-doc', 'quote', vars.documentId] }),
  })
}

/** 创建/恢复 AI 文书商业购买单；返回 payBizType/payBizNo 后再调公共支付 */
export function usePayDocumentOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['ai-doc', 'order', 'pay'],
    mutationFn: async (body: LegalAiDocumentOrderPayBo) =>
      http.post<LegalAiDocumentCheckoutVo>(`${AI_CLIENT_BASE}/document/order/pay`, body),
    onSuccess: (_, vars) => {
      if (vars.payOrderNo) {
        qc.invalidateQueries({ queryKey: ['ai-doc', 'order', vars.payOrderNo] })
      }
      if (vars.documentId) {
        qc.invalidateQueries({ queryKey: ['ai-doc', 'quote', vars.documentId] })
      }
    },
  })
}

/** Web 微信预支付：统一支付接口只接收业务单号，金额/描述由后端解析 */
export function useWechatPrepay() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['pay', 'wechat', 'prepay'],
    mutationFn: async (body: PayWechatPrepayRequestBo) =>
      http.post<PayWechatPrepayVo>('/app/pay/wechat/prepay', body),
    onSuccess: (data) => {
      if (data.payOrderNo) {
        qc.invalidateQueries({ queryKey: ['ai-doc', 'order', data.payOrderNo] })
      }
    },
  })
}

/** 会员免费解锁 */
export function useUnlockDocumentOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['ai-doc', 'unlock'],
    mutationFn: async (body: LegalAiDocumentCheckoutBo) =>
      http.post<LegalAiDocumentCheckoutVo>(`${AI_CLIENT_BASE}/document/order/unlock`, body),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['ai-doc', 'quote', vars.documentId] }),
  })
}

export function resolveBackendUrl(url: string): string {
  const normalized = url.trim()
  if (!normalized || /^[a-z][a-z\d+.-]*:/i.test(normalized)) return normalized
  if (normalized.startsWith('/')) return `${apiBaseURL}${normalized}`
  return normalized
}

export function resolveAuthenticatedRequestUrl(url: string): string {
  const normalized = url.trim()
  if (!normalized) return normalized
  if (/^https?:\/\//i.test(normalized)) {
    const parsed = new URL(normalized)
    const apiOrigin = resolveApiOrigin()
    if (parsed.origin === window.location.origin || (apiOrigin && parsed.origin === apiOrigin)) {
      return normalizeAxiosRequestUrl(`${parsed.pathname}${parsed.search}${parsed.hash}`)
    }
  }
  return normalizeAxiosRequestUrl(normalized)
}

export async function fetchAiDocumentPreviewAsset(url: string): Promise<Blob> {
  return http.get<Blob>(resolveAuthenticatedRequestUrl(url), { responseType: 'blob' })
}

export interface DownloadAiDocumentFileInput {
  documentId?: string | null
  title?: string | null
}

/** 下载 AI 文书。后端下载接口必须走鉴权请求，不能直接 window.open。 */
export async function downloadAiDocumentFile({
  documentId,
  title,
}: DownloadAiDocumentFileInput): Promise<void> {
  const normalizedDocumentId = documentId?.trim()
  if (!normalizedDocumentId) {
    throw new Error('暂无下载链接')
  }

  const blob = await http.get<Blob>(
    `/client/legal/document/download/${encodeURIComponent(normalizedDocumentId)}`,
    { responseType: 'blob' },
  )
  await assertDownloadBlob(blob)
  saveBlobAsFile(blob, resolveDocumentFileName(title))
}

function resolveApiOrigin(): string | null {
  if (!/^https?:\/\//i.test(apiBaseURL)) return null
  return new URL(apiBaseURL).origin
}

function normalizeAxiosRequestUrl(url: string): string {
  if (!url.startsWith('/')) return url

  const apiPath = getConfiguredApiBasePath()
  if (apiPath !== '/' && url === apiPath) return '/'
  if (apiPath !== '/' && url.startsWith(`${apiPath}/`)) return url.slice(apiPath.length)
  return url
}

function getConfiguredApiBasePath(): string {
  if (/^https?:\/\//i.test(apiBaseURL)) {
    const pathname = new URL(apiBaseURL).pathname.replace(/\/$/, '')
    return pathname || '/'
  }
  return apiBaseURL.replace(/\/$/, '') || '/'
}

async function assertDownloadBlob(blob: Blob) {
  if (!blob.type.includes('json') && !blob.type.startsWith('text/')) return

  const text = await blob.text()
  try {
    const payload = JSON.parse(text) as { code?: number; msg?: string }
    if (typeof payload.code === 'number' && payload.code !== 200) {
      throw new BizError(payload.code, payload.msg || '下载失败')
    }
  } catch (error) {
    if (error instanceof BizError) throw error
  }
}

function saveBlobAsFile(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  anchor.rel = 'noopener'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

function resolveDocumentFileName(title: string | null | undefined): string {
  const base = (title?.trim() || 'AI法律文书')
    .replace(/[\\/:*?"<>|\r\n\t]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
  return /\.[a-z0-9]{2,8}$/i.test(base) ? base : `${base}.docx`
}

/* ---------- AI Conversation ---------- */

/** AI 会话状态: 0 进行中 / 1 已结束 / 2 已超时 / 3 已关闭 */
export const AI_SESSION_STATUS = {
  ACTIVE: 0,
  ENDED: 1,
  TIMEOUT: 2,
  CLOSED: 3,
} as const

export function countActiveGuestConversations(
  sessions: LegalAiConversationMetaVo[],
  now = Date.now(),
): number {
  return sessions.filter((session) => {
    if (Number(session.status) !== AI_SESSION_STATUS.ACTIVE) return false
    if (!session.expiresAt) return true
    const expiresAt = Date.parse(session.expiresAt.replace(' ', 'T'))
    return !Number.isFinite(expiresAt) || expiresAt > now
  }).length
}

/** 我的 AI 会话列表 */
export function useMyAiSessions(page: PageQuery = {}, opts: { enabled?: boolean } = {}) {
  const subjectKey = useAiConversationSubjectKey()
  return useQuery({
    queryKey: ['ai-doc', 'conversations', subjectKey, page],
    queryFn: () =>
      http.get<TableDataInfo<LegalAiConversationMetaVo>>(AI_CONVERSATION_BASE, { params: page }),
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  })
}

/** AI 会话详情 */
export function useAiSessionByUuid(
  conversationId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  const subjectKey = useAiConversationSubjectKey()
  return useQuery({
    queryKey: ['ai-doc', 'conversation', subjectKey, conversationId],
    queryFn: () =>
      http.get<LegalAiConversationMetaVo>(
        `${AI_CONVERSATION_BASE}/${encodeURIComponent(conversationId ?? '')}`,
      ),
    enabled: (opts.enabled ?? true) && Boolean(conversationId),
    staleTime: 30_000,
  })
}

/** 修改 AI 会话标题 */
export function useRenameAiSession() {
  const qc = useQueryClient()
  const subjectKey = useAiConversationSubjectKey()
  return useMutation({
    mutationKey: ['ai-doc', 'conversation', 'rename'],
    mutationFn: async ({ conversationId, title }: { conversationId: string; title: string }) =>
      http.put<LegalAiConversationMetaVo>(
        `${AI_CONVERSATION_BASE}/${encodeURIComponent(conversationId)}/title`,
        { title } satisfies LegalAiConversationTitleRequest,
      ),
    onSuccess: (conversation) => {
      if (conversation.conversationId) {
        qc.setQueryData(
          ['ai-doc', 'conversation', subjectKey, conversation.conversationId],
          conversation,
        )
      }
      qc.invalidateQueries({ queryKey: ['ai-doc', 'conversations', subjectKey] })
    },
  })
}

function updatePinnedSessionCache(
  data: TableDataInfo<LegalAiConversationMetaVo> | undefined,
  conversation: LegalAiConversationMetaVo,
): TableDataInfo<LegalAiConversationMetaVo> | undefined {
  if (!data?.rows) return data
  return {
    ...data,
    rows: data.rows.map((session) =>
      session.conversationId === conversation.conversationId ? conversation : session,
    ),
  }
}

/** 置顶/取消置顶 AI 会话 */
export function usePinAiSession() {
  const qc = useQueryClient()
  const subjectKey = useAiConversationSubjectKey()
  return useMutation({
    mutationKey: ['ai-doc', 'conversation', 'pin'],
    mutationFn: async ({ conversationId, pinned }: { conversationId: string; pinned: boolean }) =>
      http.put<LegalAiConversationMetaVo>(
        `${AI_CONVERSATION_BASE}/${encodeURIComponent(conversationId)}/pin`,
        { pinned } satisfies LegalAiConversationPinRequest,
      ),
    onSuccess: (conversation) => {
      qc.setQueriesData<TableDataInfo<LegalAiConversationMetaVo>>(
        { queryKey: ['ai-doc', 'conversations', subjectKey] },
        (data) => updatePinnedSessionCache(data, conversation),
      )
      if (conversation.conversationId) {
        qc.setQueryData(
          ['ai-doc', 'conversation', subjectKey, conversation.conversationId],
          conversation,
        )
      }
    },
  })
}

/** 删除 AI 会话 */
export function useDeleteAiSession() {
  const qc = useQueryClient()
  const subjectKey = useAiConversationSubjectKey()
  return useMutation({
    mutationKey: ['ai-doc', 'conversation', 'delete'],
    mutationFn: async (conversationId: string) =>
      http.delete<void>(`${AI_CONVERSATION_BASE}/${encodeURIComponent(conversationId)}`),
    onSuccess: (_, conversationId) => {
      qc.removeQueries({
        queryKey: ['ai-doc', 'conversation', subjectKey, conversationId],
      })
      qc.removeQueries({ queryKey: ['ai-doc', 'messages', subjectKey, conversationId] })
      qc.invalidateQueries({ queryKey: ['ai-doc', 'conversations', subjectKey] })
    },
  })
}

/** AI 会话消息 */
export function fetchAiSessionMessagesByUuid(
  conversationId: string,
  page: PageQuery = {},
): Promise<TableDataInfo<LegalAiMessageVo>> {
  return http.get<TableDataInfo<LegalAiMessageVo>>(
    `${AI_CONVERSATION_BASE}/${encodeURIComponent(conversationId)}/messages`,
    { params: page },
  )
}

export function useAiSessionMessagesByUuid(
  conversationId: string | undefined,
  page: PageQuery = {},
  opts: { enabled?: boolean } = {},
) {
  const subjectKey = useAiConversationSubjectKey()
  return useQuery({
    queryKey: ['ai-doc', 'messages', subjectKey, conversationId, page],
    queryFn: () => fetchAiSessionMessagesByUuid(conversationId ?? '', page),
    enabled: (opts.enabled ?? true) && Boolean(conversationId),
    staleTime: 0,
  })
}

export function issueAiGuestAccess(
  request: LegalAiGuestAccessRequest = {},
): Promise<LegalAiGuestAccessVo> {
  return http.post<LegalAiGuestAccessVo>('/app/legal/ai/access/guest', request)
}

export function claimAiConversation(conversationId: string): Promise<LegalAiConversationMetaVo> {
  return http.post<LegalAiConversationMetaVo>(
    `${AI_CONVERSATION_BASE}/${encodeURIComponent(conversationId)}/claim`,
  )
}

export function getAiGuestAssetPolicy(conversationId?: string): Promise<LegalAiGuestAssetPolicyVo> {
  return http.get<LegalAiGuestAssetPolicyVo>('/app/legal/ai/assets/policy', {
    params: conversationId ? { conversationId } : undefined,
  })
}

export interface UploadAiGuestAssetInput {
  file: File
  conversationId?: string
  mediaDuration?: number
  onProgress?: (percent: number) => void
}

export function uploadAiGuestAsset({
  file,
  conversationId,
  mediaDuration,
  onProgress,
}: UploadAiGuestAssetInput): Promise<LegalAiGuestAssetVo> {
  const formData = new FormData()
  formData.append('file', file, file.name)
  return http.post<LegalAiGuestAssetVo>('/app/legal/ai/assets', formData, {
    params: {
      ...(conversationId ? { conversationId } : {}),
      ...(mediaDuration ? { mediaDuration } : {}),
    },
    onUploadProgress: (event) => {
      if (!event.total || !onProgress) return
      onProgress(Math.round((event.loaded * 100) / event.total))
    },
  })
}

export function discardAiGuestAsset(assetId: string): Promise<void> {
  return http.delete<void>(`/app/legal/ai/assets/${encodeURIComponent(assetId)}`)
}

// 转写服务最长等待120秒，额外预留上传和响应传输时间。
const AI_DOC_VOICE_TIMEOUT_MS = 150_000

interface AiDocVoiceRecognizePayload {
  text?: string
  extract_text?: string
  results?: Array<{
    text?: string
    extract_text?: string
    status?: string
  }>
  payload?: AiDocVoiceRecognizePayload
  [key: string]: unknown
}

function collectTextractText(payload: AiDocVoiceRecognizePayload): string {
  const directText =
    typeof payload.text === 'string'
      ? payload.text.trim()
      : typeof payload.extract_text === 'string'
        ? payload.extract_text.trim()
        : ''

  if (directText) return directText

  const resultText = (payload.results ?? [])
    .filter((item) => item && (item.status === 'success' || !item.status))
    .map((item) => {
      if (typeof item.text === 'string') return item.text.trim()
      if (typeof item.extract_text === 'string') return item.extract_text.trim()
      return ''
    })
    .filter(Boolean)
    .join('\n')

  if (resultText) return resultText
  return payload.payload ? collectTextractText(payload.payload) : ''
}

export interface AiDocVoiceRecognizeOptions {
  isGuest?: boolean
  conversationId?: string
  mediaDuration?: number
}

/** 语音识别：按当前身份选择转写接口，返回文字供输入框插入。 */
export function recognizeAiDocVoice(file: File, options: AiDocVoiceRecognizeOptions = {}) {
  const formData = new FormData()
  formData.append('file', file, file.name)
  if (options.isGuest) {
    return http
      .post<LegalAiVoiceTranscriptionVo>('/app/legal/ai/assets/transcriptions', formData, {
        params: { conversationId: options.conversationId, mediaDuration: options.mediaDuration },
        timeout: AI_DOC_VOICE_TIMEOUT_MS,
      })
      .then((payload) => ({ text: payload.text?.trim() ?? '', payload }))
  }
  formData.append('scene', 'legal_app_ai_doc_voice')
  return http
    .post<AiDocVoiceRecognizePayload>('/client/legal/textract/single', formData, {
      timeout: AI_DOC_VOICE_TIMEOUT_MS,
    })
    .then((payload) => ({ text: collectTextractText(payload), payload }))
}
